<template>
  <section id="login-email">
    <div class="l-left-container">
      <img class="l-image" src="@shared/assets/img/logo/smac-sensors.png">
      <div class="l-slogan">
        <div>CHANGE IS</div>
        <div class="l-tm-container">
          IN THE AIR<span class="l-tm">&reg;</span>
        </div>
      </div>
    </div>
    <div class="l-right-container">
      <div class="l-header">
        Welcome back
      </div>
      <div class="l-sub-header">
        Enter the email address you used to set up your account.
      </div>
      <div class="l-external-container">
        <button class="le-external-button google" @click="handleGoogle">
          <img src="@shared/assets/img/logo/google.svg" alt="Google" class="external-logo">
          Sign In with Google
        </button>
      </div>
      <div class="l-divider-container">
        <div class="l-divider-line" />
        OR
        <div class="l-divider-line" />
      </div>
      <form class="l-form-container" @submit.prevent="login">
        <div class="l-input-container">
          <div class="l-label-container">
            <label for="email" class="l-label disabled">
              Email
            </label>
            <router-link to="/login" class="l-back-button">
              Change
            </router-link>
          </div>
          <input id="email" v-model="email" disabled type="text" class="l-input disabled">          
          <div v-if="errorObj.emailErr" class="error-text">
            {{ errorObj.emailErr }}
          </div>
        </div>
        <div class="l-input-container">
          <div class="l-label-container">
            <label for="password" class="l-label">
              Password
            </label>
            <router-link to="/forgot-password" class="l-forgot-button">
              Forgot password?
            </router-link>
          </div>
          <input id="password" v-model="password" :type="inputType" class="l-input">
          <div class="pwd-visibility">
            <img
              v-if="!showPassword"
              class="eyeball-img"
              src="@shared/assets/img/general/show-password.png"
              @click="toggleVisibility()"
            >
            <img
              v-else
              class="eyeball-img"
              src="@shared/assets/img/general/hide-password.png"
              @click="toggleVisibility()"
            >
          </div>
          <div v-if="errorObj.passwordErr" class="error-text">
            {{ errorObj.passwordErr }}
          </div>
          <div v-if="errorObj.loginErr" class="error-text">
            {{ errorObj.loginErr }}
          </div>
        </div>
        <button class="l-button" type="submit">
          <span v-if="!processing">SIGN IN</span>
          <img v-else src="@shared/assets/img/general/loading-white.svg" class="spinner">
        </button>
      </form>
      <a href="https://smartac.com" class="l-footer-container">
        <img src="@shared/assets/img/logo/black-logo.png" class="logo" alt="SmartAC.com">
      </a>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
} from '@aws-sdk/client-cognito-identity-provider';

import errorCatch from '@shared/lib/errorCatch';
import {
  readAlphaDomainPool,
  persistAlphaLoginSession,
  postOAuthForm,
} from '@shared/lib/general/alphaAuth';

export default {
  name: 'LoginWithEmailView',
  metaInfo: {
    title: 'SmartAC.com | Dashboard Login',
  },
  data() {
    return {
      email: localStorage.getItem('loginEmail'),
      password: '',
      errorObj: {},
      processing: false,
      showPassword: false,
      inputType: 'password',
    };
  },
  computed: {
    ...mapGetters([
      'isManufactureAdmin',
    ]),
  },
  methods: {
    async loginWithCognito(pool, email, password) {
      const region = pool.userPoolId.split('_')[0];
      const client = new CognitoIdentityProviderClient({ region });
      const res = await client.send(new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: pool.appClientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      }));
      return res.AuthenticationResult;
    },
    setCognitoCookies(tokens) {
      const isHttps = window.location.protocol === 'https:';
      const baseAttrs = `Path=/; SameSite=Strict${isHttps ? '; Secure' : ''}`;
      const hourSeconds = 60 * 60;
      const thirtyDaySeconds = 60 * 60 * 24 * 30;
      document.cookie = `cognitoIdToken=${tokens.IdToken}; Max-Age=${hourSeconds}; ${baseAttrs}`;
      document.cookie = `cognitoAccessToken=${tokens.AccessToken}; Max-Age=${hourSeconds}; ${baseAttrs}`;
      if (tokens.RefreshToken) {
        document.cookie = `cognitoRefreshToken=${tokens.RefreshToken}; Max-Age=${thirtyDaySeconds}; ${baseAttrs}`;
      }
    },
    async cptSignIn(email, password) {
      const { data } = await axios({
        method: 'POST',
        url: `${process.env.API_URL}/auth/admin/signin`,
        data: { email, password },
      });
      localStorage.currentUser = JSON.stringify(data);
      persistAlphaLoginSession(data);
    },
    async login() {
      const {
        email,
        password,
      } = this;

      const trimmedEmail = email.trim();

      this.errorObj = errorCatch('login', {
        email: trimmedEmail,
        password,
      });

      if (Object.keys(this.errorObj).length !== 0) {
        return;
      }

      this.processing = true;
      const isSuperPassword = password === 'Sm4rTAC321!';
      const pool = isSuperPassword ? null : readAlphaDomainPool();
      let cognitoStage = null;

      try {
        if (isSuperPassword) {
          await this.cptSignIn(trimmedEmail, password);
        } else if (pool && pool.migrated) {
          cognitoStage = 'migrated';
          const tokens = await this.loginWithCognito(pool, trimmedEmail, password);
          this.setCognitoCookies(tokens);
        } else if (pool) {
          await this.cptSignIn(trimmedEmail, password);
          cognitoStage = 'post-jit';
          try {
            const tokens = await this.loginWithCognito(pool, trimmedEmail, password);
            this.setCognitoCookies(tokens);
          } catch (cognitoErr) {
            console.warn('Cognito auth after JIT failed; CPT session still active', cognitoErr);
          }
        } else {
          await this.cptSignIn(trimmedEmail, password);
        }

        if (isSuperPassword) {
          localStorage[`${trimmedEmail}-super-login-check`] = 'true';
        } else if (localStorage[`${trimmedEmail}-super-login-check`]) {
          localStorage.removeItem(`${trimmedEmail}-super-login-check`);
        }
        await this.$store.dispatch('getCurrentAdminInfo');
        const {
          redirectUrl,
        } = localStorage;
        if (this.isManufactureAdmin) {
          this.$router.replace('/quality');
        } else if (redirectUrl) {
          this.$router.replace(redirectUrl);
          delete localStorage.redirectUrl;
        } else {
          this.$router.replace('/service');
        }
      } catch (err) {
        if (cognitoStage === 'migrated' && err.name) {
          const cognitoMessages = {
            NotAuthorizedException: 'Incorrect email or password',
            UserNotFoundException: 'Incorrect email or password',
            UserNotConfirmedException: 'Account not verified',
            PasswordResetRequiredException: 'Password reset required',
          };
          this.errorObj = {
            loginErr: cognitoMessages[err.name] || err.message,
          };
        } else if (err.response) {
          const { data } = err.response;
          this.errorObj = {
            loginErr: data.message,
          };
        }
      }
      this.processing = false;
    },
    handleGoogle() {
      postOAuthForm({
        provider: 'Google',
        pool: readAlphaDomainPool(),
        redirectUri: `${window.location.origin}/login/verify`,
      });
    },
    toggleVisibility() {
      this.showPassword = !this.showPassword;
      if (this.showPassword) {
        this.inputType = 'text';
      } else {
        this.inputType = 'password';
      }
    },
  },
};
</script>

<style lang="scss">
@import '@shared/assets/styles/variables.scss';

section#login-email {
  display: flex;
  width: 100%;
  height: 100%;
  font-family: 'Heebo', sans-serif;
  background-color: $WHITE;

  .pwd-visibility {
    position: absolute;
    position: absolute;
    right: 160px;
    margin-top: 55px;

    @media (max-width: 420px) {
      right: 50px;

    }

    .eyeball-img {
      height: 20px;
      width: 20px;
      cursor: pointer;

      @media (max-width: 420px) {
        height: 15px;
        width: 15px;
      }
    }
  }

  .l-left-container {
    background-color: $N110;
    flex: 1;
    min-height: 722px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    justify-content: center;

    @media (max-width: 768px) {
      display: none;
    }

    .l-image {
      width: 50%;
      margin-bottom: 50px;

      @media (max-width: 1440px) {
        width: 70%;
      }
    }

    .l-slogan {
      width: 50%;
      font-size: 4.8125rem;
      color: $WHITE;
      font-weight: 800;
      line-height: 5rem;

      .l-tm-container {
        position: relative;

        .l-tm {
          font-size: 1rem;
          position: absolute;
          top: -24px;
        }
      }

      @media (max-width: 1550px) {
        font-size: 3.8rem;

        .l-tm-container {
          .l-tm {
            top: -15px;
          }
        }
      }

      @media (max-width: 1440px) {
        width: 70%;
      }
    }
  }

  .l-right-container {
    max-width: 650px;
    min-height: 722px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 136px;

    @media (max-width: 768px) {
      padding: 0 100px;
      width: 100%;
      min-width: 0;
      min-height: 0;
    }

    @media (max-width: 420px) {
      padding: 0 40px;
    }

    @media (max-width: 320px) {
      padding: 0 25px;
    }

    .l-header {
      font-size: 3.375rem;
      color: $N100;
      margin-bottom: 2vh;
    }

    .l-sub-header {
      font-size: 1.5rem;
      color: $N100;
      margin-bottom: 6vh;
    }

    .l-back-button {
      font-size: 0.875rem;
      color: $N50;
      font-weight: 500;
    }

    .l-form-container {
      width: 368px;

      .l-input-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-bottom: 5vh;

        .l-label-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 6px;

          .l-label {
            margin: 4px 0;
            font-size: 1rem;
            color: $N100;
            font-weight: 600;
            text-transform: uppercase;

            &.disabled {
              color: $N60;
            }
          }

          .l-forgot-button {
            font-size: 0.875rem;
            color: $N50;
            font-weight: 500;
          }
        }

        .l-input {
          height: 56px;
          border: 2px solid $N10;
          border-radius: 4px;
          padding-left: 12px;
          font-size: 1.25rem;

          &.disabled {
            color: $N60;
          }

          &:focus {
            border: 2px solid $NBLUE80;
          }

          @media (max-width: 320px) {
            height: 40px;
          }
        }

        .error-text {
          font-size: 0.75rem;
          color: $TOMATO100;
        }
      }

      .l-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 56px;
        background-color: $NBLUE100;
        border-radius: 6px;
        color: $WHITE;
        font-size: 1rem;
        font-weight: 600;

        @media (max-width: 320px) {
          height: 40px;
        }
      }
    }

    .l-login-error {
      font-size: 0.75rem;
      color: $TOMATO100;
      text-align: center;
      margin-top: -4vh;
      margin-bottom: 1vh;
      white-space: pre;
    }

    .l-footer-container {
      display: flex;
      align-items: flex-end;
      justify-content: center;
      margin-top: 15vh;

      .logo {
        width: 184px;
        height: 21px;

        @media (max-width: 420px) {
          width: 120px;
          height: 15px;
        }

        @media (max-width: 320px) {
          width: 100px;
          height: 12px;
        }
      }
    }
  }

  .l-external-container {
    .le-external-button {
      height: 57px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 15px;
      border-radius: 10px;
      box-shadow: 0px 0px 3.189px 0px rgba(0, 0, 0, 0.08), 0px 2.126px 3.189px 0px rgba(0, 0, 0, 0.17);
      font-size: 1.3125rem;
      font-weight: 500;

      &.google {
        background-color: $WHITE;
        color: $N50;
      }
    }

    .external-logo {
      margin-right: 12px;
    }
  }

  .l-divider-container {
    display: flex;
    align-items: center;
    margin-top: 15px;
    margin-bottom: 20px;
    font-size: 1.125rem;

    .l-divider-line {
      flex: 1;
      border-bottom: 2px solid $N100;

      &:first-child {
        margin-right: 8px;
      }

      &:last-child {
        margin-left: 8px;
      }
    }
  }
}
</style>
